import type { OutreachMessage, OutreachReplyChip } from '@discovr/types';

/** AI reply chips shown in the composer's pill row. Counter is the composer's own pill and is not counted. */
export const REPLY_CHIP_LIMIT = 2;

/** The parse finishes asynchronously on a webhook; after this a missing parse is a lost one, not a pending one. */
export const PARSE_PENDING_MS = 90_000;

/** Go's kind=reply NO_SUGGESTION reasons when the AI wrote no suggestion. */
export type ReplyChipsUnavailableReason = 'no_parse' | 'no_ai_chip';

/** none: nothing is owed (no inbound reply, or it was answered since). escalated: the agent read the reply and wrote no chips because it handed the thread to a person. */
export type ReplyChipsState =
  | { status: 'pending' }
  | { status: 'ready'; chips: OutreachReplyChip[] }
  | { status: 'unavailable'; reason: ReplyChipsUnavailableReason }
  | { status: 'escalated' }
  | { status: 'none' };

const NONE: ReplyChipsState = { status: 'none' };
/** Outbound statuses that mean nobody answered: a cancelled or failed message never reached the creator. */
const NOT_AN_ANSWER: ReadonlySet<string> = new Set(['cancelled', 'failed']);

const latestRealReplyIndex = (rows: readonly OutreachMessage[]) => {
  let at = -1;
  rows.forEach((m, i) => {
    if (m.direction === 'in' && !m.isAutoReply && !m.isDsn) at = i;
  });
  return at;
};

/** The row's escalation was raised on or after this reply arrived, so it is the reply's own. */
function escalatedSince(reply: OutreachMessage, escalatedAt?: string): boolean {
  const raised = escalatedAt ? Date.parse(escalatedAt) : NaN;
  const received = reply.receivedAt ? Date.parse(reply.receivedAt) : NaN;
  return Number.isFinite(raised) && Number.isFinite(received) && raised >= received;
}

/** AI reply chips for the latest real inbound reply, while nobody has answered it yet. Template chips are not the AI's and are dropped. */
export function replyChipsFor(rows: readonly OutreachMessage[], limit: number = REPLY_CHIP_LIMIT, nowMs: number = Date.now(), escalatedAt?: string): ReplyChipsState {
  const at = latestRealReplyIndex(rows);
  const target = rows[at];
  if (!target) return NONE;
  if (rows.slice(at + 1).some((m) => m.direction === 'out' && !NOT_AN_ANSWER.has(m.status))) return NONE;

  const written = (target.parsed?.suggestions ?? []).filter((c) => c.source === 'llm');
  if (written.length > 0) {
    // Counter and Confirm rate are the composer's own pills, so a suggestion for either is dropped.
    const chips = written.filter((c) => c.action !== 'counter' && c.action !== 'confirm_rate').slice(0, limit);
    return chips.length > 0 ? { status: 'ready', chips } : NONE;
  }
  if (target.parseError) return { status: 'unavailable', reason: 'no_parse' };
  if (target.parsed) {
    if (escalatedSince(target, escalatedAt)) return { status: 'escalated' };
    return target.intent === 'auto_reply' || target.intent === 'unsubscribe' ? NONE : { status: 'unavailable', reason: 'no_ai_chip' };
  }
  const awaiting = !target.intent;
  const received = target.receivedAt ? Date.parse(target.receivedAt) : NaN;
  if (!awaiting || !Number.isFinite(received)) return NONE;
  return nowMs - received <= PARSE_PENDING_MS ? { status: 'pending' } : { status: 'unavailable', reason: 'no_parse' };
}
